// ProjectDecisions.js
import { useMemo, useState } from "react";
import {
  Paper,
  Typography,
  Stack,
  Box,
  Backdrop,
  Portal,
  Chip,
  IconButton,
} from "@mui/material";
import {
  deleteProjectProgress,
  updateProjectProgress,
} from "../../Controllers/projectController";
import ProgressForm from "./Progress/progressForm";
import HourglassBottomRounded from "@mui/icons-material/HourglassBottomRounded";
import Section from "./section";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SectorCircularProgress from "./Progress/sectorCircularProgress";
import { progressColor } from "../helpers";

const monthMap = {
  0: "January",
  1: "February",
  2: "March",
  3: "April",
  4: "May",
  5: "June",
  6: "July",
  7: "August",
  8: "September",
  9: "October",
  10: "November",
  11: "December",
};

function normalizeDecimal(value) {
  if (typeof value !== "string") return null;

  let v = value.trim();

  if (!/^-?(?:\d+([.,]\d{0,2})?|[.,]\d{1,2})$/.test(v)) {
    return null;
  }

  // Replace comma with dot
  v = v.replace(",", ".");

  // Handle leading dot: ".5" → "0.5"
  if (v.startsWith(".")) {
    v = "0" + v;
  } else if (v.startsWith("-.")) {
    v = v.replace("-.", "-0.");
  }

  // Handle trailing dot: "123." → "123.0"
  if (v.endsWith(".")) {
    v = v + "0";
  }

  return v;
}

const ProjectProgress = ({
  project,
  projectProgress = [],
  refreshProject,
  project_id,
  project_start,
  project_end,
  setAlert,
  setError,
  isAdmin,
  isAccounting,
  isProjectManager,
}) => {
  const [input, setInput] = useState(0);
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(isProjectManager);
  const [hovered, setHovered] = useState(null);

  const requiredReports = useMemo(() => {
    let reports = [];
    if (editing) {
      const lastReport = projectProgress[0];
      const start = new Date(lastReport.start);
      const end = new Date(lastReport.end);
      reports.push({
        project_progress_id: lastReport.project_progress_id,
        start: start,
        end: end,
        month: monthMap[end.getUTCMonth()],
        year: start.getUTCFullYear(),
        min_total_progress:
          Number.parseFloat(lastReport.total_progress) -
          Number.parseFloat(lastReport.progress),
        total_progress: Number.parseFloat(lastReport.total_progress),
        progress: Number.parseFloat(lastReport.progress),
      });
      setInput(Number.parseFloat(lastReport.progress));
      setOpen(true);
      return reports;
    }
    const lastReportEndDate = new Date(
      projectProgress.length === 0 ? project_start : projectProgress[0].end,
    );
    const lastReportEndDatePlussOneDay = new Date(
      lastReportEndDate.setDate(lastReportEndDate.getDate() + 1),
    );
    const total_progress =
      projectProgress.length === 0 ? 0 : projectProgress[0].total_progress;
    if (total_progress === "100.00") {
      return [];
    }
    const ym = (d) => d.getUTCFullYear() * 12 + d.getUTCMonth();

    const fromDate = lastReportEndDatePlussOneDay;
    const nowDate = new Date();

    // compute range in months (inclusive)
    const startYM = ym(fromDate);
    const toYM = ym(nowDate);
    const lastReportEndDateDay = lastReportEndDatePlussOneDay.getDate();
    for (let idx = startYM; idx < toYM; idx++) {
      const y = Math.floor(idx / 12);
      const m = idx % 12;
      const start = new Date(
        Date.UTC(
          y,
          m,
          lastReportEndDateDay === 1 ? 1 : lastReportEndDateDay - 1,
          0,
          0,
          0,
        ),
      );

      const end = new Date(Date.UTC(y, m + 1, 0, 23, 59, 59));
      reports.push({
        start: start,
        end: end,
        month: monthMap[end.getUTCMonth()],
        year: start.getUTCFullYear(),
        min_total_progress: Number.parseFloat(total_progress),
        total_progress: Number.parseFloat(total_progress),
        progress: 0,
      });
    }
    return reports;
  }, [projectProgress, project_start, project_end, editing]);

  const value = useMemo(
    (prev) => {
      const report = requiredReports[0];
      if (report === undefined) return 0;
      const parsed =
        typeof input === "string" ? normalizeDecimal(input) : input;
      let val = Number.parseFloat(parsed);
      if (isNaN(val)) return prev ?? report.min_total_progress;
      val =
        val < 0
          ? 0
          : val > 100 - report.min_total_progress
            ? 100 - report.min_total_progress
            : val;
      report.total_progress = val + report.min_total_progress;
      report.progress = report.total_progress - report.min_total_progress;
      return report.total_progress;
    },
    [input, requiredReports],
  );

  function onChange(val) {
    const report = requiredReports[0];
    if (val < 0) val = 0;
    if (val > 100 - report.min_total_progress)
      val = Math.round((100 - report.min_total_progress) * 100) / 100;
    setInput(val);
  }

  async function onSubmit() {
    const report = requiredReports[0];
    const success = await updateProjectProgress(
      report.project_progress_id,
      project_id,
      report.progress,
      report.comments,
      setAlert,
    );
    if (success) {
      await refreshProject();
      setInput(0);
      setEditing(false);
    }
  }
  function onCancel() {
    if (editing) setEditing(false);
    setOpen(false);
  }

  async function onDelete(project_progress_id) {
    if (!window.confirm("Are you sure you want to delete project progress?"))
      return;
    const success = await deleteProjectProgress(project_progress_id, setAlert);
    if (success) {
      await refreshProject();
    }
  }

  return (
    <Section
      title="PROGRESS REPORTS"
      chips={[
        (i) =>
          requiredReports.length > 0 && (
            <Box key={i} sx={{ pt: { xs: 0, sm: 1 }, pb: { xs: 1, sm: 0 } }}>
              <Chip
                size="small"
                color={"warning"}
                variant={"filled"}
                icon={<HourglassBottomRounded />}
                label={`${requiredReports.length} Require${
                  requiredReports.length === 1 ? "s" : ""
                } Submitting`}
              />
            </Box>
          ),
        (i) => (
          <Box key={i} sx={{ pt: { xs: 0, sm: 1 }, pb: { xs: 1, sm: 0 } }}>
            <Chip
              size="small"
              variant="outlined"
              label={`${projectProgress.length} Submitted Reports`}
            />
          </Box>
        ),
      ]}
      spacing={0}
    >
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5}>
        {requiredReports.length !== 0 &&
          (open ? (
            <Portal>
              {/* dim EVERYTHING */}
              <Backdrop
                open
                sx={{
                  zIndex: (t) => t.zIndex.tooltip + 1,
                  bgcolor: "rgba(0,0,0,0.35)", // tint color/opacity
                }}
              />
              <Box
                sx={{
                  position: "fixed",
                  zIndex: (t) => t.zIndex.tooltip + 1,
                  inset: 0,
                  pointerEvents: "none",
                }}
              >
                <Box
                  sx={{
                    pointerEvents: "auto",
                    m: 3 /* place it how you want */,
                  }}
                >
                  <ProgressForm
                    row={requiredReports[0]}
                    i={0}
                    value={value}
                    onChange={onChange}
                    input={input}
                    onSubmit={onSubmit}
                    onCancel={onCancel}
                    open={open}
                  ></ProgressForm>
                </Box>
              </Box>
            </Portal>
          ) : (
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5}>
              {requiredReports.map((row, i) => {
                return (
                  <ProgressForm
                    key={i}
                    row={row}
                    i={i}
                    value={value}
                    onChange={onChange}
                    input={input}
                    onSubmit={onSubmit}
                    onCancel={onCancel}
                    open={open}
                  ></ProgressForm>
                );
              })}
            </Stack>
          ))}

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: (t) => `1px solid ${t.palette.divider}`,
            bgcolor: (t) => t.palette.action.hover,
            m: 0.5,
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
          }}
        >
          <SectorCircularProgress
            values={projectProgress.map((r) => ({
              value: r.progress,
              total: r.total_progress,
            }))}
            label={`${project.total_progress ?? 0}%`}
            size={200}
            thickness={50}
            fontSize={24}
            hovered={hovered}
            setHovered={setHovered}
          ></SectorCircularProgress>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: (t) => `1px solid ${t.palette.divider}`,
            bgcolor: (t) => t.palette.action.hover,
            m: 0.5,
            width: 210,
            height: 210,
            flexGrow: 1,
          }}
        >
          <Stack
            direction="column"
            overflow={{ md: "", lg: "auto" }}
            flexGrow={1}
            height="100%"
            sx={{ px: 2 }}
            /* paddingLeft={{ md: 0, lg: 5 }}
            paddingTop={{ md: 5, lg: 0 }} */
          >
            {projectProgress.map((row, i) => {
              const start_date = new Date(row.start);
              const date = `${String(start_date.getUTCMonth() + 1).padStart(2, "0")}.${start_date.getUTCFullYear()}`;
              return (
                <Stack
                  direction="row"
                  key={row.project_progress_id}
                  sx={{ pl: 1 }}
                  onPointerEnter={() =>
                    setHovered((i - projectProgress.length + 1) * -1)
                  }
                  onPointerLeave={() => setHovered(null)}
                  style={{
                    cursor: "pointer",
                    transform:
                      hovered === (i - projectProgress.length + 1) * -1
                        ? "scale(1.05)"
                        : "scale(1)",
                    transformOrigin: "center",
                    transition: "transform 0.15s",
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      bgcolor: progressColor(row.total_progress),
                      mt: 1.25,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 18,
                      color: progressColor(row.total_progress),
                      pl: 1,
                      pt: 0.5,
                    }}
                  >
                    {date}: {row.progress}%
                  </Typography>

                  <Stack
                    direction="row"
                    sx={{
                      alignItems: "center",
                      justifyContent: "end",
                      ml: "auto",
                    }}
                  >
                    {new Date(row.locked_at) > new Date() && (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => setEditing(!editing)}
                        tabIndex={-1}
                      >
                        <EditIcon fontSize="small" color="error" />
                      </IconButton>
                    )}
                    {(isAdmin || isAccounting) && (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => onDelete(row.project_progress_id)}
                        tabIndex={-1}
                      >
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    )}
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Paper>
      </Stack>
    </Section>
  );
};

export default ProjectProgress;
